// AGC: NewTask - Create new task form
// AGC: All data comes from Firebase - no hardcoded mock data
// AGC: Logs activity to notifications when task is created
// AGC: Enhanced with inputs section, machinery picker, map preview, and assignee dropdown
import React, { useState, useEffect, useMemo } from 'react'
import { TopBar, Button } from '../shared'
import { getFields } from '../../services/fields'
import { useQueryClient } from '@tanstack/react-query'
import { createTask } from '../../services/tasks'
import { logTaskActivity } from '../../services/activityLogger'
import { getAssignableUsers, type AssigneeOption } from '../../services/assigneesService'
import { getActiveInputs, createInputAndInventoryItem } from '../../services/inputsService'
import { getMachines, createMachine } from '../../services/machinesService'
import { useAuth } from '../../hooks/useAuth'
import { TASK_CATEGORIES, type Screen, type TaskPriority, type TaskCategory, type Field, type TaskInputEntry } from '../../types'
import { INPUT_CATEGORIES, type InputMaster } from '../../types/inventory'
import { MACHINE_CATEGORIES, MACHINE_BRANDS, FARM_MACHINES, type MachineCategory } from '../../types/machine'
import type { MachineInstance } from '../../types/machineInstance'
import { getRule, normalizeMachineCategory } from '@droidfarm/shared/activityRules'
import { useInventory } from '../../hooks/useInventory'
import { useCustomList } from '../../hooks/useCustomList'
import { STORAGE_KEY_CUSTOM_TASK_TYPES } from '../settings/TarefasSettings'
import { LotMapPreviewCard } from '../fields/LotMapPreviewCard'
import { useLotes } from '../../context'
import { clampDecimals } from '../../utils'
import { v4 as uuidv4 } from 'uuid'
import { Tractor, AlertTriangle, Plus, ChevronDown, Loader2, User, X, Package, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from '../../hooks/useTranslation'
import { useFormTelemetry } from '../../hooks/useFormTelemetry'
import { track } from '../../lib/analytics'

interface NewTaskProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
  preselectedPlotId?: string // AGC: Optional - pre-select this plot when coming from lot page
}

interface TaskFormData {
  title: string
  description: string
  fieldId: string
  plotId: string
  category: TaskCategory | ''
  priority: TaskPriority
  dueDate: string
  assignedTo: string       // AGC: Display name for UI
  assignedToId: string     // AGC: Firebase UID - required for task notifications
  inputs: TaskInputEntry[]
  machinery: Array<{ id: string; name: string; category: string }>
}

/**
 * AGC: NewTask - Form to create a new task
 * AGC: Enhanced with inputs, machinery, map preview, and assignee dropdown
 */
export const NewTask: React.FC<NewTaskProps> = ({ onNavigate, onBack, preselectedPlotId }) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const queryClient = useQueryClient()
  // AGC: Form telemetry — tracks task_create form submit success/fail
  const { trackSubmit } = useFormTelemetry('task_create')
  const [fields, setFields] = useState<Field[]>([])
  const [isLoadingFields, setIsLoadingFields] = useState(true)
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    fieldId: '',
    plotId: '',
    category: '',
    priority: 'medium',
    dueDate: '',
    assignedTo: '',
    assignedToId: '',
    inputs: [],
    machinery: [],
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // AGC: Inputs state
  const [availableInputs, setAvailableInputs] = useState<InputMaster[]>([])
  const [isLoadingInputs, setIsLoadingInputs] = useState(false)
  const [showInputDropdown, setShowInputDropdown] = useState(false)
  // AGC: Catalog modal state — search + category filter
  const [showCatalogModal, setShowCatalogModal] = useState(false)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string>('all')
  const [newInputEntry, setNewInputEntry] = useState<{ inputId: string; quantity: string; isCustom: boolean; customName: string; customUnit: string }>({ inputId: '', quantity: '', isCustom: false, customName: '', customUnit: 'L' })
  const [showNewInputModal, setShowNewInputModal] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [registerForm, setRegisterForm] = useState({ name: '', category: 'other', unit: 'L', costPerUnit: 0, initialQuantity: 0, minimumStock: 0 })
  // AGC: Stock prompt state — shown when catalog input has no inventory entry
  const [stockPrompt, setStockPrompt] = useState<{ inputId: string; inputName: string; unit: string; category: string; pendingEntry: TaskInputEntry } | null>(null)
  const [stockPromptQty, setStockPromptQty] = useState('')
  const { items: inventoryItems, ensureItemExists, recordMovement } = useInventory()

  // AGC: Machinery state
  const [userMachines, setUserMachines] = useState<MachineInstance[]>([])
  const [machineryMode, setMachineryMode] = useState<'self-propelled' | 'trator-implement'>('self-propelled')
  const [openSlot, setOpenSlot] = useState<number | null>(null)
  const [showNewMachineModal, setShowNewMachineModal] = useState(false)
  const [isRegisteringMachine, setIsRegisteringMachine] = useState(false)
  const [machineForm, setMachineForm] = useState({ name: '', category: 'trator' as MachineCategory, catalogId: '', brand: '', model: '', year: '' })

  // AGC: Assignee state
  const [availableAssignees, setAvailableAssignees] = useState<AssigneeOption[]>([])
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false)
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false)

  // AGC: Lot context for area calculations and map preview
  const { plots: lotes } = useLotes()
  // AGC: Resolve selected lot — try plotId first, then find plot from selectedField
  const selectedField = fields.find(f => f.id === formData.fieldId)
  const selectedPlot = formData.plotId
    ? (lotes || []).find(l => l.id === formData.plotId) || selectedField?.plots?.find((p: any) => p.id === formData.plotId)
    : null
  const currentLot = selectedPlot || selectedField?.plots?.[0] || null
  const lotAreaHa = currentLot?.area || selectedField?.totalHectares || 1

  // AGC: Filtered catalog inputs for the search modal
  const filteredCatalogInputs = useMemo(() => {
    let filtered = availableInputs
    if (catalogCategoryFilter !== 'all') {
      filtered = filtered.filter(i => i.category === catalogCategoryFilter)
    }
    if (catalogSearch.trim()) {
      const search = catalogSearch.toLowerCase().trim()
      filtered = filtered.filter(i => i.name.toLowerCase().includes(search))
    }
    return filtered
  }, [availableInputs, catalogCategoryFilter, catalogSearch])

  // AGC: Merge built-in task categories with user-defined ones from localStorage
  const customTaskTypes = useCustomList(STORAGE_KEY_CUSTOM_TASK_TYPES)
  const mergedTaskCategories = useMemo(
    () => [
      ...TASK_CATEGORIES.map((c) => ({ id: c.id as string, name: c.name, icon: c.icon, iconImg: c.iconImg })),
      ...customTaskTypes.map((c) => ({ id: c.id, name: c.label, icon: '📋' })),
    ],
    [customTaskTypes]
  )

  // AGC: Load fields from Firebase for the authenticated user
  useEffect(() => {
    const loadFields = async () => {
      try {
        setIsLoadingFields(true)
        const token = await getAuthToken()
        const data = await getFields(token)

        console.log('📋 AGC: NewTask - Loaded', data.length, 'field(s) from Firebase')
        console.log('📋 AGC: NewTask - preselectedPlotId:', preselectedPlotId)

        // AGC: Log all fields and their plots for debugging
        data.forEach((field, idx) => {
          console.log(`   Field ${idx + 1}: ${field.name} (ID: ${field.id}) - ${field.plots?.length || 0} plot(s)`)
          if (field.plots) {
            field.plots.forEach((plot: any, pIdx: number) => {
              console.log(`      Plot ${pIdx + 1}: ${plot.name} (ID: ${plot.id})`)
            })
          }
        })

        setFields(data)

        // AGC: CRITICAL FIX - If preselectedPlotId is provided, find and pre-select the field/plot
        // AGC: If NOT provided (coming from dashboard), automatically select first field
        // AGC: This ensures the plot dropdown always has options
        if (data.length > 0) {
          if (preselectedPlotId) {
            // AGC: Case 1: Coming from lot page - pre-select specific lot
            console.log('🎯 AGC: NewTask - Pre-selecting specific plot:', preselectedPlotId)

            let found = false
            for (const field of data) {
              const plot = field.plots?.find((p: any) => p.id === preselectedPlotId)
              if (plot) {
                console.log('✅ AGC: Found plot in field:', field.name, '/', plot.name)
                setFormData(prev => ({
                  ...prev,
                  fieldId: field.id,
                  plotId: preselectedPlotId,
                }))
                found = true
                break
              }
            }

            if (!found) {
              console.warn('⚠️ AGC: Could not find plot with ID:', preselectedPlotId)
              // AGC: Fallback - select first field so user can see plots
              console.log('⚠️ AGC: Falling back to first field')
              setFormData(prev => ({
                ...prev,
                fieldId: data[0].id,
              }))
            }
          } else {
            // AGC: Case 2: Coming from dashboard — auto-select first field + first plot
            const firstPlot = data[0].plots?.[0]
            setFormData(prev => ({
              ...prev,
              fieldId: data[0].id,
              plotId: firstPlot?.id || '',
            }))
          }
        } else {
          console.warn('⚠️ AGC: No fields found for user')
        }
      } catch (error) {
        console.error('❌ AGC: Error loading fields:', error)
        setFields([])
      } finally {
        setIsLoadingFields(false)
      }
    }

    loadFields()
  }, [getAuthToken, preselectedPlotId])

  // AGC: Get plots for selected field (selectedField defined above in lot context)
  const availablePlots = selectedField?.plots || []

  // AGC: Load available inputs from admin catalog
  useEffect(() => {
    const loadInputs = async () => {
      try {
        setIsLoadingInputs(true)
        const token = await getAuthToken()
        setAvailableInputs(await getActiveInputs(token))
      } catch {
        // AGC: Silently fail — inputs section will show empty state
      } finally {
        setIsLoadingInputs(false)
      }
    }
    loadInputs()
  }, [getAuthToken])

  // AGC: Load user's machinery fleet
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const token = await getAuthToken()
        const list = await getMachines(token)
        if (!cancelled) setUserMachines(Array.isArray(list) ? list : [])
      } catch (e) {
        console.warn('AGC: [NewTask] load machines failed', e)
        if (!cancelled) setUserMachines([])
      }
    }
    load()
    return () => { cancelled = true }
  }, [getAuthToken])

  // AGC: Load assignable users (team members + self)
  // AGC: Refreshes when team member is created (listens to 'team-member-created' event)
  useEffect(() => {
    const loadAssignees = async () => {
      try {
        setIsLoadingAssignees(true)
        const assignees = await getAssignableUsers(user)
        setAvailableAssignees(assignees)
        // AGC: Default to current user (self) if not already set
        if (!formData.assignedToId && assignees.length > 0) {
          const selfUser = assignees.find(a => a.isCurrentUser)
          if (selfUser) {
            setFormData(prev => ({
              ...prev,
              assignedTo: selfUser.displayName || selfUser.email || 'Eu',
              assignedToId: selfUser.uid,
            }))
          }
        }
      } catch (err) {
        console.error('❌ AGC: Error loading assignable users:', err)
      } finally {
        setIsLoadingAssignees(false)
      }
    }
    if (user?.uid) {
      loadAssignees()

      // AGC: Listen for team member creation events to refresh assignees list
      const handleTeamMemberCreated = async () => {
        console.log('🔄 AGC: Team member created event received - refreshing assignees list')
        await new Promise(resolve => setTimeout(resolve, 500))
        await loadAssignees()
      }

      window.addEventListener('team-member-created', handleTeamMemberCreated as EventListener)

      return () => {
        window.removeEventListener('team-member-created', handleTeamMemberCreated as EventListener)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])

  // AGC: Finalize adding an input entry to the task form
  const commitInputEntry = (entry: TaskInputEntry) => {
    const currentInputs = formData.inputs || []
    setFormData(prev => ({ ...prev, inputs: [...currentInputs, entry] }))
    setNewInputEntry({ inputId: '', quantity: '', isCustom: false, customName: '', customUnit: 'L' })
    setShowInputDropdown(false)
  }

  // AGC: Handle adding an input entry (catalog input) — checks inventory
  const handleAddInput = () => {
    const quantity = parseFloat(newInputEntry.quantity)

    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('tasks.enterValidQuantity'))
      return
    }

    let newEntry: TaskInputEntry

    if (newInputEntry.isCustom) {
      if (!newInputEntry.customName.trim()) {
        toast.error(t('tasks.enterInputName'))
        return
      }

      newEntry = {
        inputId: `custom-${uuidv4()}`,
        inputName: newInputEntry.customName.trim(),
        quantityPerHa: clampDecimals(quantity / lotAreaHa),
        totalQuantity: clampDecimals(quantity),
        unit: newInputEntry.customUnit || 'un',
        inputSnapshot: {
          name: newInputEntry.customName.trim(),
          category: 'other',
          unit: newInputEntry.customUnit || 'un',
          capturedAt: new Date().toISOString(),
        },
        isCustom: true,
        isDiscontinued: false,
      }
      commitInputEntry(newEntry)
      return
    }

    // AGC: Catalog input
    if (!newInputEntry.inputId) {
      toast.error(t('tasks.selectInputAndQuantity'))
      return
    }

    const selectedInput = availableInputs.find(i => i.id === newInputEntry.inputId)
    if (!selectedInput) {
      toast.error(t('tasks.inputNotFound'))
      return
    }

    newEntry = {
      inputId: selectedInput.id,
      inputName: selectedInput.name,
      quantityPerHa: clampDecimals(quantity / lotAreaHa),
      totalQuantity: clampDecimals(quantity),
      unit: selectedInput.unit || 'un',
      inputSnapshot: {
        name: selectedInput.name,
        category: selectedInput.category || 'other',
        unit: selectedInput.unit || 'un',
        capturedAt: new Date().toISOString(),
      },
      isCustom: false,
      isDiscontinued: !selectedInput.isActive,
    }

    // AGC: Check if this input exists in inventory
    const inInventory = inventoryItems.some(inv =>
      inv.inputId === selectedInput.id ||
      inv.name.toLowerCase() === selectedInput.name.toLowerCase()
    )
    if (!inInventory) {
      setStockPrompt({
        inputId: selectedInput.id,
        inputName: selectedInput.name,
        unit: selectedInput.unit || 'un',
        category: selectedInput.category || 'other',
        pendingEntry: newEntry,
      })
      setStockPromptQty('')
      return
    }

    commitInputEntry(newEntry)
  }

  // AGC: Handle stock prompt — create inventory entry + add input to task
  const handleStockPromptConfirm = async () => {
    if (!stockPrompt) return
    const qty = parseFloat(stockPromptQty)
    try {
      const item = await ensureItemExists(
        stockPrompt.inputId,
        stockPrompt.inputName,
        stockPrompt.category as any,
        stockPrompt.unit
      )
      if (!isNaN(qty) && qty > 0) {
        await recordMovement(item.id, {
          type: 'entry',
          quantity: qty,
          reason: 'Estoque inicial',
        })
      }
      toast.success(t('tasks.stockCreated', { name: stockPrompt.inputName }))
    } catch {
      toast.error(t('tasks.stockCreateError'))
    }
    commitInputEntry(stockPrompt.pendingEntry)
    setStockPrompt(null)
  }

  // AGC: Skip stock prompt — add input to task without inventory
  const handleStockPromptSkip = () => {
    if (!stockPrompt) return
    commitInputEntry(stockPrompt.pendingEntry)
    setStockPrompt(null)
  }

  // AGC: Handle removing an input entry
  const handleRemoveInput = (index: number) => {
    const currentInputs = formData.inputs || []
    setFormData(prev => ({
      ...prev,
      inputs: currentInputs.filter((_, i) => i !== index)
    }))
  }

  // AGC: Register new product → admin catalog + inventory, then auto-select in input form
  const handleRegisterNewInput = async () => {
    if (!registerForm.name.trim()) return
    setIsRegistering(true)
    try {
      const token = await getAuthToken()
      const result = await createInputAndInventoryItem({
        name: registerForm.name.trim(),
        category: registerForm.category,
        unit: registerForm.unit,
        costPerUnit: registerForm.costPerUnit,
        initialQuantity: registerForm.initialQuantity,
        minimumStock: registerForm.minimumStock,
      }, token)
      if (result) {
        // AGC: Refresh available inputs to include the new catalog entry
        const refreshed = await getActiveInputs(token)
        setAvailableInputs(refreshed)
        // AGC: Auto-select the new input in the form
        setNewInputEntry(prev => ({ ...prev, inputId: result.inputId, isCustom: false }))
        toast.success(`${registerForm.name} cadastrado!`, { icon: '✅' })
      }
      setShowNewInputModal(false)
      setRegisterForm({ name: '', category: 'other', unit: 'L', costPerUnit: 0, initialQuantity: 0, minimumStock: 0 })
    } catch (err) {
      toast.error('Erro ao cadastrar produto')
    } finally {
      setIsRegistering(false)
    }
  }

  // AGC: Register new machine → creates via API, refreshes list, auto-selects
  const handleRegisterNewMachine = async () => {
    if (!machineForm.name.trim()) return
    setIsRegisteringMachine(true)
    try {
      const token = await getAuthToken()
      const result = await createMachine({
        name: machineForm.name.trim(),
        category: machineForm.category,
        catalogId: machineForm.catalogId || undefined,
        brand: machineForm.brand || undefined,
        model: machineForm.model || undefined,
        year: machineForm.year ? parseInt(machineForm.year) : undefined,
        horimeterHours: 0,
        status: 'available',
      }, token)
      if (result) {
        const refreshed = await getMachines(token)
        setUserMachines(refreshed)
        // AGC: Auto-select the new machine in the task
        const newMachine = refreshed.find((m: { name: string }) => m.name === machineForm.name.trim())
        if (newMachine) {
          setFormData(prev => ({
            ...prev,
            machinery: [{ id: newMachine.id, name: newMachine.name, category: newMachine.category }],
          }))
        }
        toast.success(`${machineForm.name} cadastrada!`, { icon: '🚜' })
      }
      setShowNewMachineModal(false)
      setMachineForm({ name: '', category: 'trator', catalogId: '', brand: '', model: '', year: '' })
    } catch (err) {
      toast.error('Erro ao cadastrar máquina')
    } finally {
      setIsRegisteringMachine(false)
    }
  }

  // AGC: Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório'
    }

    if (!formData.fieldId) {
      newErrors.fieldId = 'Selecione um campo'
    }

    if (!formData.category) {
      newErrors.category = 'Selecione uma categoria'
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Data de vencimento é obrigatória'
    }

    if (!formData.assignedToId) {
      newErrors.assignedTo = 'Selecione um responsável'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // AGC: Handle create task - save to Firebase with userId
  const handleCreate = async () => {
    if (!validate()) {
      return
    }

    try {
      setIsSubmitting(true)
      const token = await getAuthToken()

      // AGC: Get field and plot names
      const field = fields.find(f => f.id === formData.fieldId)
      const plot = field?.plots?.find(p => p.id === formData.plotId)

      // AGC: Create task data
      console.log('📝 AGC: NewTask - Creating task with plotId:', formData.plotId)
      console.log('📝 AGC: NewTask - preselectedPlotId was:', preselectedPlotId)

      // AGC: assignedToId (UID) is required for task assignment notifications to reach the assignee
      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim() || '',
        fieldId: formData.fieldId,
        fieldName: field?.name || '',
        plotId: formData.plotId || undefined,
        plotName: plot?.name || undefined,
        category: formData.category,
        status: 'pending' as const,
        priority: formData.priority,
        progress: 0,
        dueDate: formData.dueDate,
        assignedTo: formData.assignedTo,
        assignedToId: formData.assignedToId || null,
        assignedToAvatar: undefined,
        createdBy: user?.uid,
        inputs: formData.inputs || [],
        machinery: formData.machinery || [],
        timeline: [
          {
            id: `tl-${Date.now()}`,
            date: new Date().toISOString(),
            status: 'pending' as const,
            user: 'Sistema',
            notes: 'Tarefa criada',
          },
        ],
      }

      // AGC: Save to Firebase via telemetry-wrapped call — emits form_submit_success/fail
      const taskCreatedAt = Date.now()
      const createdTask = await trackSubmit(() => createTask(taskData, token))
      void track('entity_created', {
        entity_type: 'task',
        source: 'form',
        duration_ms: Date.now() - taskCreatedAt,
      })

      // AGC: Log activity to notifications (activity log entry, not an alert)
      // AGC: This will appear in the notifications page but NOT in the bell badge
      await logTaskActivity(
        'created',
        formData.title.trim(),
        'click',  // AGC: Source is click (UI interaction)
        token,
        createdTask?.id,
        formData.category || undefined
      )

      toast.success('Tarefa criada com sucesso!', {
        icon: '✅',
        duration: 3000,
      })

      // AGC: Invalidate tasks cache so list refetches immediately on mount
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      window.dispatchEvent(new CustomEvent('task-created'))

      // AGC: If we came from a lot page (preselectedPlotId), go back instead of tasks list
      if (preselectedPlotId) {
        onBack()
      } else {
        onNavigate('tasks')
      }
    } catch (error: any) {
      console.error('AGC: Error creating task:', error)
      toast.error(error.message || 'Erro ao criar tarefa. Tente novamente.', {
        icon: '❌',
        duration: 3000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Selected assignee for display
  const selectedAssignee = availableAssignees.find(a => a.uid === formData.assignedToId)

  // AGC: Scroll to top on mount so title field is visible — delayed to run after layout
  useEffect(() => {
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }))
    const timer = setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }), 300)
    return () => clearTimeout(timer)
  }, [])

  // AGC: Close dropdowns on outside tap or page scroll — no blocking overlay needed
  useEffect(() => {
    const closeAll = () => { setShowInputDropdown(false); setShowAssigneeDropdown(false); setOpenSlot(null) }
    const handleOutsideTap = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-dropdown]')) closeAll()
    }
    document.addEventListener('touchstart', handleOutsideTap, true)
    document.addEventListener('mousedown', handleOutsideTap, true)
    window.addEventListener('scroll', closeAll, true)
    return () => {
      document.removeEventListener('touchstart', handleOutsideTap, true)
      document.removeEventListener('mousedown', handleOutsideTap, true)
      window.removeEventListener('scroll', closeAll, true)
    }
  }, [])

  return (
    <div className="min-h-screen app-bg pb-24">
      {/* AGC: Top Bar */}
      <TopBar
        title={t('tasks.newTask')}
        onBack={onBack}
      />

      {/* AGC: Form */}
      <div className="px-4 py-4 space-y-4">
        {/* AGC: Title Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('tasks.taskName')} *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={t('tasks.titlePlaceholder')}
            className={`input ${errors.title ? 'input-error' : ''}`}
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* AGC: Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('tasks.description')}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('tasks.descriptionPlaceholder')}
            rows={3}
            className="input resize-none"
          />
        </div>

        {/* AGC: Farm Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('tasks.farm')} *
          </label>
          <select
            value={formData.fieldId}
            onChange={(e) => setFormData({ ...formData, fieldId: e.target.value, plotId: '' })}
            className={`select ${errors.fieldId ? 'input-error' : ''}`}
          >
            <option value="">{t('tasks.selectField')}</option>
            {isLoadingFields ? (
              <option value="" disabled>{t('common.loading')}...</option>
            ) : fields.length === 0 ? (
              <option value="" disabled>{t('tasks.noFieldsRegistered')}</option>
            ) : (
              fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.name}
                </option>
              ))
            )}
          </select>
          {errors.fieldId && (
            <p className="text-red-500 text-sm mt-1">{errors.fieldId}</p>
          )}
        </div>

        {/* AGC: Plot/Lot Selection */}
        {formData.fieldId && availablePlots.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fields.lot')}
            </label>
            <select
              value={formData.plotId}
              onChange={(e) => setFormData({ ...formData, plotId: e.target.value })}
              className="select"
            >
              <option value="">{t('tasks.selectField')}</option>
              {availablePlots.map((plot) => (
                <option key={plot.id} value={plot.id}>
                  {plot.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* AGC: Map preview when field/lot is selected — key forces remount on lot change */}
        {(() => {
          const mapLot = currentLot || (selectedField?.plots?.[0] as any)
          if (!mapLot?.coordinates || mapLot.coordinates.length < 3) return null
          const coords = mapLot.coordinates
          const centerLat = coords.reduce((sum: number, c: number[]) => sum + c[0], 0) / coords.length
          const centerLng = coords.reduce((sum: number, c: number[]) => sum + c[1], 0) / coords.length
          return (
            <div key={`map-${mapLot.id}-${formData.plotId}-${formData.fieldId}`} className="mt-2 rounded-xl overflow-hidden border border-slate-200">
              <div className="pointer-events-none">
                <LotMapPreviewCard
                  mapData={{
                    lotId: mapLot.id,
                    polygon: coords,
                    totalHectares: mapLot.area || selectedField?.totalHectares || 0,
                    centerCoordinates: { latitude: centerLat, longitude: centerLng },
                  }}
                  onPress={() => {}}
                />
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-white">
                <div>
                  <p className="text-sm font-medium text-gray-900">{mapLot.name || selectedField?.name}</p>
                  {selectedField && <p className="text-xs text-gray-500">{selectedField.name}</p>}
                </div>
                <span className="text-sm font-semibold text-emerald-600">{(mapLot.area || selectedField?.totalHectares || 0).toFixed(1)} ha</span>
              </div>
            </div>
          )
        })()}

        {/* AGC: Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('tasks.category')} *
          </label>
          <div className="grid grid-cols-4 gap-2">
            {mergedTaskCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setFormData({ ...formData, category: cat.id as TaskCategory })}
                className={`
                  relative rounded-xl border-2 transition-all duration-200 overflow-hidden aspect-square
                  ${formData.category === cat.id
                    ? 'border-[#0B5F4A] ring-2 ring-emerald-400 scale-[1.03]'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                style={{ backgroundColor: '#fdfae8' }}
              >
                {cat.iconImg
                  ? <img src={cat.iconImg} alt={cat.name} className="absolute w-[80%] h-[70%] object-contain left-1/2 -translate-x-1/2 top-1" />
                  : <span className="text-3xl absolute inset-0 flex items-center justify-center pb-6">{cat.icon}</span>
                }
                <span className={`absolute bottom-0 inset-x-0 text-center text-[10px] font-semibold py-1 z-10 ${
                  formData.category === cat.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-black/50 text-white'
                }`}>{cat.name}</span>
              </button>
            ))}
          </div>
          {errors.category && (
            <p className="text-red-500 text-sm mt-1">{errors.category}</p>
          )}
        </div>

        {/* AGC: Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('tasks.priority')}
          </label>
          <div className="flex gap-2">
            {(['low', 'medium', 'high'] as TaskPriority[]).map((priority) => {
              const labels = { low: t('tasks.priorityLow'), medium: t('tasks.priorityMedium'), high: t('tasks.priorityHigh') }
              const colors = {
                low: 'bg-gray-100 text-gray-700 border-gray-200',
                medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                high: 'bg-red-100 text-red-700 border-red-200',
              }
              const activeColors = {
                low: 'bg-gray-500 text-white border-gray-500',
                medium: 'bg-yellow-500 text-white border-yellow-500',
                high: 'bg-red-500 text-white border-red-500',
              }

              return (
                <button
                  key={priority}
                  type="button"
                  onClick={() => setFormData({ ...formData, priority })}
                  className={`
                    flex-1 py-2 rounded-xl border-2 font-medium transition-all duration-200
                    ${formData.priority === priority ? activeColors[priority] : colors[priority]}
                  `}
                >
                  {labels[priority]}
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Inputs (Insumos) Section */}
        <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
          {/* AGC: Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('tasks.inputs')}
              </h3>
            </div>
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
              {(formData.inputs || []).length} {t('tasks.items')}
            </span>
          </div>

          {/* AGC: Inputs Table */}
          {(formData.inputs || []).length > 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-3">
              {/* AGC: Table Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <div className="col-span-5">{t('tasks.inputName')}</div>
                <div className="col-span-2 text-right">{t('tasks.qty')}</div>
                <div className="col-span-2">{t('tasks.unit')}</div>
                <div className="col-span-2 text-right">/ha</div>
                <div className="col-span-1"></div>
              </div>

              {/* AGC: Table Body */}
              {(formData.inputs || []).map((input, index) => {
                const isDiscontinued = (input as TaskInputEntry).isDiscontinued === true
                const isCustom = (input as TaskInputEntry).isCustom === true
                const totalQty = input.totalQuantity
                const perHaQty = input.quantityPerHa

                return (
                  <div
                    key={index}
                    className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center border-b border-slate-100 last:border-b-0 ${
                      isDiscontinued ? 'bg-amber-50/50' : ''
                    }`}
                  >
                    {/* AGC: Input Name with badges */}
                    <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                      <span className="text-sm text-gray-900 truncate">{input.inputName}</span>
                      {isCustom && (
                        <span className="flex-shrink-0 px-1 py-0.5 text-[10px] bg-blue-100 text-blue-600 rounded">
                          {t('tasks.custom')}
                        </span>
                      )}
                      {isDiscontinued && (
                        <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      )}
                    </div>

                    {/* AGC: Quantity */}
                    <div className="col-span-2 text-right">
                      <span className="text-sm font-medium text-gray-900">{clampDecimals(Number(totalQty) || 0)}</span>
                    </div>

                    {/* AGC: Unit */}
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600">{input.unit}</span>
                    </div>

                    {/* AGC: Per Hectare */}
                    <div className="col-span-2 text-right">
                      <span className="text-xs text-gray-500">
                        {perHaQty !== null && perHaQty !== undefined ? Number(perHaQty).toFixed(2) : '-'}
                      </span>
                    </div>

                    {/* AGC: Remove Button */}
                    <div className="col-span-1 flex justify-end">
                      <button
                        onClick={() => handleRemoveInput(index)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 text-sm mb-3">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {t('tasks.noInputsAdded')}
            </div>
          )}

          {/* AGC: Add Input Section */}
          <div className="border-t border-slate-200 pt-3">
            {/* AGC: Source Toggle */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setNewInputEntry(prev => ({ ...prev, isCustom: false, inputId: '', customName: '' }))}
                className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all bg-emerald-600 text-white border-emerald-600 shadow-sm"
              >
                <Plus className="w-3 h-3 inline mr-1" />
                {t('tasks.fromCatalog')}
              </button>
              <button
                onClick={() => setShowNewInputModal(true)}
                className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all bg-white text-gray-600 border-slate-300 hover:bg-slate-50"
              >
                <Plus className="w-3 h-3 inline mr-1" />
                {t('tasks.newInput')}
              </button>
            </div>

            {/* AGC: Catalog Input Selection — button opens search modal */}
            <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
              <button
                onClick={() => { setCatalogSearch(''); setCatalogCategoryFilter('all'); setShowCatalogModal(true) }}
                className="w-full px-3 py-2 border border-slate-300 bg-slate-50 rounded-lg flex items-center justify-between hover:border-slate-400 transition-colors"
              >
                <span className="text-sm text-gray-700">
                  {newInputEntry.inputId
                    ? availableInputs.find(i => i.id === newInputEntry.inputId)?.name
                    : t('tasks.selectInput')}
                </span>
                <Search className="w-4 h-4 text-gray-400" />
              </button>

              <div className="flex gap-2">
                <input
                  type="number"
                  value={newInputEntry.quantity}
                  onChange={(e) => setNewInputEntry(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder={t('tasks.totalQuantity')}
                  className="flex-1 px-3 py-2 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                />
                <button
                  onClick={handleAddInput}
                  disabled={!newInputEntry.inputId || !newInputEntry.quantity}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                >
                  {t('common.add')}
                </button>
              </div>

              {lotAreaHa > 0 && newInputEntry.quantity && (
                <p className="text-xs text-gray-500 text-center mt-2 pt-2 border-t border-slate-100">
                  ≈ {(parseFloat(newInputEntry.quantity) / lotAreaHa).toFixed(2)} {availableInputs.find(i => i.id === newInputEntry.inputId)?.unit || 'un'}/ha
                  {' '}({t('tasks.for')} {lotAreaHa.toFixed(1)} ha)
                </p>
              )}
            </div>

            {/* AGC: Catalog Search Modal — search + category filter */}
            {showCatalogModal && (
              <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-10">
                <div className="bg-white w-full max-w-md rounded-2xl max-h-[88vh] overflow-hidden mx-4 flex flex-col">
                  <div className="flex items-center justify-between p-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-semibold text-gray-900">{t('tasks.selectInput')}</h3>
                    </div>
                    <button onClick={() => setShowCatalogModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  {/* AGC: Search input */}
                  <div className="px-3 pt-2 pb-2">
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={catalogSearch}
                        onChange={(e) => setCatalogSearch(e.target.value)}
                        placeholder="Buscar insumo..."
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  {/* AGC: Category filter chips — large touch targets, snap-scroll */}
                  <div className="px-3 pt-2 pb-4 flex gap-2 overflow-x-auto hide-scrollbar snap-x snap-mandatory scroll-pl-3 min-h-[52px] items-center">
                    <button
                      onClick={() => setCatalogCategoryFilter('all')}
                      className={`flex-shrink-0 px-4 py-2.5 rounded-full text-base font-medium whitespace-nowrap transition-colors snap-start ${
                        catalogCategoryFilter === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Todos
                    </button>
                    {INPUT_CATEGORIES.filter(c => availableInputs.some(i => i.category === c.id)).map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setCatalogCategoryFilter(cat.id)}
                        className={`flex-shrink-0 px-4 py-2.5 rounded-full text-base font-medium whitespace-nowrap transition-colors snap-start ${
                          catalogCategoryFilter === cat.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat.namePt}
                      </button>
                    ))}
                  </div>

                  {/* AGC: Results list */}
                  <div className="flex-1 overflow-y-auto border-t border-gray-100">
                    {isLoadingInputs ? (
                      <div className="p-6 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </div>
                    ) : filteredCatalogInputs.length === 0 ? (
                      <div className="p-6 text-center text-gray-500 text-sm">
                        {t('tasks.noInputsAvailable')}
                      </div>
                    ) : (
                      filteredCatalogInputs.map((input) => (
                        <button
                          key={input.id}
                          onClick={() => {
                            setNewInputEntry(prev => ({ ...prev, inputId: input.id }))
                            setShowCatalogModal(false)
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center justify-between border-b border-gray-50 ${
                            newInputEntry.inputId === input.id ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">{input.name}</p>
                            <p className="text-xs text-gray-500">
                              {INPUT_CATEGORIES.find(c => c.id === input.category)?.namePt || input.category} · {input.unit || 'un'}
                            </p>
                          </div>
                          {newInputEntry.inputId === input.id && (
                            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* AGC: Stock prompt — shown when catalog input has no inventory entry */}
            {stockPrompt && (
              <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center px-6">
                <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900 text-base">{stockPrompt.inputName}</h3>
                    <p className="text-sm text-gray-500 mt-1">{t('tasks.noInventoryEntry')}</p>
                  </div>
                  <div className="p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tasks.initialStock')} ({stockPrompt.unit})</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={stockPromptQty}
                      onChange={(e) => setStockPromptQty(e.target.value)}
                      placeholder="0"
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3 p-4 pt-0">
                    <button
                      onClick={handleStockPromptSkip}
                      className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
                    >
                      {t('tasks.skipStock')}
                    </button>
                    <button
                      onClick={handleStockPromptConfirm}
                      className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                    >
                      {t('tasks.addStock')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* AGC: Register New Product Modal — anchored to top for keyboard visibility */}
            {showNewInputModal && (
              <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-14">
                <div className="bg-white w-full max-w-md rounded-2xl max-h-[90vh] overflow-y-auto mx-4">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-emerald-600" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{t('tasks.registerNewProduct')}</h3>
                        <p className="text-xs text-gray-500">{t('tasks.registerNewProductDesc')}</p>
                      </div>
                    </div>
                    <button onClick={() => setShowNewInputModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.itemName')} *</label>
                      <input
                        type="text"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ex: Glifosato, Ureia..."
                        className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.category')}</label>
                      <select
                        value={registerForm.category}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                      >
                        {INPUT_CATEGORIES.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.namePt}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.unit')}</label>
                      <select
                        value={registerForm.unit}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, unit: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                      >
                        <option value="L">L</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="mL">mL</option>
                        <option value="un">un</option>
                        <option value="sacas">sacas</option>
                        <option value="doses">doses</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.pricePerUnit')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={registerForm.costPerUnit || ''}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, costPerUnit: parseFloat(e.target.value) || 0 }))}
                        placeholder="R$ 0,00"
                        className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.initialQuantity')}</label>
                        <input
                          type="number"
                          min="0"
                          value={registerForm.initialQuantity || ''}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, initialQuantity: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.minimumStockLabel')}</label>
                        <input
                          type="number"
                          min="0"
                          value={registerForm.minimumStock || ''}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, minimumStock: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setShowNewInputModal(false)}
                        className="flex-1 px-4 py-2.5 border border-slate-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-slate-50"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleRegisterNewInput}
                        disabled={!registerForm.name.trim() || isRegistering}
                        className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRegistering ? t('tasks.registering') : t('tasks.register')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AGC: Machinery Section — T3 combo-aware picker */}
        {formData.category && (
          <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <Tractor className="w-4 h-4 text-orange-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('tasks.machine')}
              </h3>
            </div>

            {(() => {
              const taskCat = String(formData.category || '').toLowerCase()
              const rule = getRule(taskCat)

              // AGC: Helper: pick a machine for a given slot index (persists to machinery[]).
              const pickMachine = (slotIndex: number, machine: MachineInstance | null) => {
                setFormData(prev => {
                  const arr = [...(prev.machinery || [])]
                  if (machine) {
                    arr[slotIndex] = { id: machine.id, name: machine.name, category: machine.category }
                  } else {
                    arr.splice(slotIndex, 1)
                  }
                  // AGC: Remove trailing empty slots.
                  while (arr.length > 0 && !arr[arr.length - 1]) arr.pop()
                  return { ...prev, machinery: arr }
                })
                setOpenSlot(null)
              }

              // AGC: Helper: render a single machine slot dropdown.
              const renderSlot = (
                slotIdx: number,
                filterCategory: MachineCategory | null,
                label: string,
              ) => {
                const selected = (formData.machinery || [])[slotIdx]
                // AGC: Normalize category before compare (fleet has mixed English/Portuguese)
                const candidates = filterCategory
                  ? userMachines.filter(m => normalizeMachineCategory(m.category) === filterCategory)
                  : userMachines
                return (
                  <div key={slotIdx} className="mb-2">
                    {label && <p className="text-[11px] text-gray-400 mb-1">{label}</p>}
                    <div className="relative" data-dropdown>
                      <button
                        onClick={() => setOpenSlot(openSlot === slotIdx ? null : slotIdx)}
                        className="w-full px-3 py-2 border border-slate-300 bg-white rounded-lg flex items-center justify-between text-sm hover:bg-slate-50"
                      >
                        <span className="truncate text-gray-900">
                          {selected?.name || t('tasks.selectMachine')}
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                      {openSlot === slotIdx && (
                          <div data-dropdown className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-48 overflow-y-auto">
                            <button
                              onClick={() => pickMachine(slotIdx, null)}
                              className="w-full px-3 py-2 text-left hover:bg-slate-50 text-gray-500 italic text-sm"
                            >
                              {t('tasks.noMachine')}
                            </button>
                            {candidates.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-gray-500 italic">
                                {t('tasks.noMachinesYet') || 'Nenhuma máquina cadastrada.'}
                              </div>
                            ) : (
                              candidates.map(m => (
                                <button
                                  key={m.id}
                                  onClick={() => pickMachine(slotIdx, m)}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Tractor className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                  <span className="text-sm text-gray-900 flex-1 truncate">{m.name}</span>
                                  <span className="text-xs text-gray-400">{m.category}</span>
                                </button>
                              ))
                            )}
                            {/* AGC: Quick-register machine from task */}
                            <button
                              onClick={() => { setOpenSlot(null); setShowNewMachineModal(true) }}
                              className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-100"
                            >
                              <Plus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                              <span className="text-sm text-emerald-600 font-medium">{t('tasks.newMachine')}</span>
                            </button>
                          </div>
                      )}
                    </div>
                  </div>
                )
              }

              // AGC: Helper: yellow trator-fallback banner (shown when ideal category missing).
              const tratorFallbackBanner = (idealCategory: string) => {
                const hasIdeal = userMachines.some(m => normalizeMachineCategory(m.category) === idealCategory)
                if (hasIdeal || !rule.tratorFallback) return null
                return (
                  <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-2 text-xs text-yellow-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      {t('tasks.idealMissing', { category: idealCategory })
                        || `Ideal: ${idealCategory}. Usando trator como alternativa.`}
                    </span>
                  </div>
                )
              }

              // AGC: Clear all machinery selections.
              const clearAll = () => setFormData(prev => ({ ...prev, machinery: [] }))

              // AGC: Branch on rule.mode.
              if (rule.mode === 'any') {
                // AGC: any — single flat dropdown, no filter.
                return (
                  <div>
                    {renderSlot(0, null, '')}
                    {(formData.machinery || []).length > 0 && (
                      <button onClick={clearAll} className="text-xs text-gray-400 hover:text-red-500 mt-1">
                        {t('tasks.noMachine')}
                      </button>
                    )}
                  </div>
                )
              }

              if (rule.mode === 'standalone') {
                // AGC: standalone — one slot, filtered to the single allowed category.
                const idealCat = rule.allowedCombos[0]?.[0] ?? null
                const hasIdeal = idealCat ? userMachines.some(m => normalizeMachineCategory(m.category) === idealCat) : false
                const slotCat = (idealCat && hasIdeal) ? idealCat : (rule.tratorFallback ? 'trator' as MachineCategory : idealCat)
                return (
                  <div>
                    {idealCat && tratorFallbackBanner(idealCat)}
                    {renderSlot(0, slotCat, idealCat ?? '')}
                  </div>
                )
              }

              if (rule.mode === 'combo-required') {
                // AGC: combo-required — two (or more) slots, one per category in allowedCombos[0].
                const combo = rule.allowedCombos[0] ?? []
                const missingCats = combo.filter(cat => !userMachines.some(m => normalizeMachineCategory(m.category) === cat))
                return (
                  <div>
                    {missingCats.length > 0 && (
                      <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-2 text-xs text-yellow-700">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          {t('tasks.addImplementPrompt', { category: missingCats.join(', ') })
                            || `Falta ${missingCats.join(', ')}. Adicione em Maquinário.`}
                        </span>
                      </div>
                    )}
                    {combo.map((cat, idx) =>
                      renderSlot(idx, cat, cat)
                    )}
                  </div>
                )
              }

              if (rule.mode === 'either') {
                // AGC: either — radio toggle between autopropelido and trator+implement.
                const selfCombo = rule.allowedCombos[0] ?? []
                const tratorCombo = rule.allowedCombos[1] ?? []
                const implementCat = selfCombo[0] ?? null
                const hasImplement = implementCat ? userMachines.some(m => normalizeMachineCategory(m.category) === implementCat) : false

                return (
                  <div>
                    {/* AGC: Mode radio */}
                    <div className="flex gap-2 mb-3">
                      {[
                        { val: 'self-propelled' as const, label: t('tasks.pickSelfPropelled') || 'Autopropelido' },
                        { val: 'trator-implement' as const, label: t('tasks.pickTratorImplement') || 'Trator + implemento' },
                      ].map(opt => (
                        <button
                          key={opt.val}
                          onClick={() => setMachineryMode(opt.val)}
                          className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                            machineryMode === opt.val
                              ? 'bg-orange-600 text-white border-orange-600'
                              : 'bg-white text-gray-600 border-slate-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {machineryMode === 'self-propelled' && (
                      <div>
                        {!hasImplement && implementCat && tratorFallbackBanner(implementCat)}
                        {renderSlot(0, hasImplement ? implementCat : (rule.tratorFallback ? 'trator' as MachineCategory : implementCat), implementCat ?? '')}
                      </div>
                    )}
                    {machineryMode === 'trator-implement' && (
                      <div>
                        {tratorCombo.map((cat, idx) =>
                          renderSlot(idx, cat, cat)
                        )}
                      </div>
                    )}
                  </div>
                )
              }

              // AGC: Fallback: plain dropdown for unknown modes.
              return renderSlot(0, null, '')
            })()}
          </div>
        )}

        {/* AGC: Register New Machine Modal — anchored to top for keyboard visibility */}
        {showNewMachineModal && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-14">
            <div className="bg-white w-full max-w-md rounded-2xl max-h-[90vh] overflow-y-auto mx-4">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Tractor className="w-5 h-5 text-orange-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('tasks.registerNewMachine')}</h3>
                    <p className="text-xs text-gray-500">{t('tasks.registerNewMachineDesc')}</p>
                  </div>
                </div>
                <button onClick={() => setShowNewMachineModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.machineName')} *</label>
                  <input
                    type="text"
                    value={machineForm.name}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: JD 7755, Stara Imperador..."
                    className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.machineCategory')}</label>
                  <select
                    value={machineForm.category}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, category: e.target.value as MachineCategory, catalogId: '', brand: '' }))}
                    className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                  >
                    {MACHINE_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* AGC: Sub-type selector — shows catalog machines for the selected category */}
                {FARM_MACHINES.filter(m => m.category === machineForm.category).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.type')}</label>
                    <select
                      value={machineForm.catalogId}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, catalogId: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                    >
                      <option value="">{t('tasks.selectInput')}...</option>
                      {FARM_MACHINES.filter(m => m.category === machineForm.category).map(m => (
                        <option key={m.id} value={m.id}>{m.icon} {m.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* AGC: Brand with datalist autocomplete from MACHINE_BRANDS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.machineBrand')}</label>
                  <input
                    type="text"
                    list={`new-task-brands-${machineForm.category}`}
                    value={machineForm.brand}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, brand: e.target.value }))}
                    placeholder="Ex: John Deere, Case IH..."
                    className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                  />
                  <datalist id={`new-task-brands-${machineForm.category}`}>
                    {(MACHINE_BRANDS[machineForm.category] || []).map(b => (
                      <option key={b} value={b} />
                    ))}
                  </datalist>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('tasks.machineModel')}</label>
                    <input
                      type="text"
                      value={machineForm.model}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, model: e.target.value }))}
                      placeholder="Ex: 5075E"
                      className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.year')}</label>
                    <input
                      type="number"
                      value={machineForm.year}
                      onChange={(e) => setMachineForm(prev => ({ ...prev, year: e.target.value }))}
                      placeholder="2024"
                      min="1970"
                      max="2030"
                      className="w-full px-3 py-2.5 border border-slate-300 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowNewMachineModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-slate-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleRegisterNewMachine}
                    disabled={!machineForm.name.trim() || isRegisteringMachine}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegisteringMachine ? t('tasks.registering') : t('tasks.register')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Due Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data de Vencimento *
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            min={new Date().toISOString().split('T')[0]}
            className={`input ${errors.dueDate ? 'input-error' : ''}`}
          />
          {errors.dueDate && (
            <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>
          )}
        </div>

        {/* AGC: Responsável — native select matching fazenda/talhão style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('tasks.assignee')} *
          </label>
          <select
            value={formData.assignedToId}
            onChange={(e) => {
              const uid = e.target.value
              const w = availableAssignees.find(x => x.uid === uid)
              setFormData(prev => ({
                ...prev,
                assignedToId: uid,
                assignedTo: w ? (w.displayName || w.email || 'Membro') : '',
              }))
            }}
            className={`select ${errors.assignedTo ? 'input-error' : ''}`}
          >
            <option value="">{t('tasks.selectResponsible', 'Selecione um responsável')}</option>
            {availableAssignees.map((a) => (
              <option key={a.id || a.uid} value={a.uid}>
                {a.isCurrentUser ? `${a.displayName || a.email || 'Eu'} (${t('common.me', 'Eu')})` : (a.displayName || a.email || 'Membro')}
              </option>
            ))}
          </select>
          {errors.assignedTo && (
            <p className="text-red-500 text-sm mt-1">{errors.assignedTo}</p>
          )}
        </div>
      </div>

      {/* AGC: Fixed footer above BottomNav (bottom-20 so nav doesn't cover it) */}
      <div className="fixed left-0 right-0 bottom-20 p-4 bg-white/95 backdrop-blur border-t border-gray-200 z-30">
        <Button fullWidth onClick={handleCreate} disabled={isSubmitting}>
          {isSubmitting ? t('common.saving', 'Salvando...') : t('tasks.createTask', 'Criar Tarefa')}
        </Button>
      </div>
    </div>
  )
}

export default NewTask
